// Canonical instructions for connecting a local project to Replay QA.
//
// Every user-facing surface uses this npx-based runbook. The replayqa CLI owns setup of the
// compatible frpc binary, the allowlisted local forward proxy, health reporting, and reconnects.
// Loop QA does not publish a second manual frpc/launcher workflow.

export interface ReplayQaAgentInstructionsArgs {
  projectId: string
  qaUrl: string
  targetUrl: string
}

// Host the managed CLI restricts the forward proxy to by default.
export function allowHostFor(targetUrl: string): string {
  try {
    return new URL(targetUrl).hostname || targetUrl
  } catch {
    return targetUrl
  }
}

export function targetPortFor(targetUrl: string): string | null {
  try {
    const url = new URL(targetUrl)
    if (url.port) return url.port
    if (url.protocol === 'https:') return '443'
    if (url.protocol === 'http:') return '80'
  } catch {
    // Project target URLs are validated by the API; retain a safe fallback for stale legacy data.
  }
  return null
}

export function buildReplayQaProxyCommand(args: Pick<ReplayQaAgentInstructionsArgs, 'projectId' | 'qaUrl'>): string {
  return `npx --yes replayqa proxy --project ${args.projectId} --qa-url ${args.qaUrl}`
}

export function buildReplayQaAgentInstructions(args: ReplayQaAgentInstructionsArgs): string {
  const command = buildReplayQaProxyCommand(args)
  const allowHost = allowHostFor(args.targetUrl)
  const expectedPort = targetPortFor(args.targetUrl)
  const portRequirement = expectedPort
    ? `The app is expected to be reachable on port ${expectedPort}. `
      + `Start or configure it on port ${expectedPort}; do not default to port 3000 unless the target URL itself uses port 3000.`
    : 'Use the port from the target URL; do not assume the app runs on port 3000.'
  return `# Connect this machine to Replay QA and start testing

Run these steps on the computer that can already open ${args.targetUrl}:

1. Make sure the app is running and ${args.targetUrl} loads from this computer. ${portRequirement}
2. Start the managed Replay QA tunnel and keep it running:

\`\`\`sh
${command}
\`\`\`

The CLI uses your existing Replay login, installs the compatible frpc binary when needed, starts an
allowlisted local forward proxy, and waits for the tunnel to become ready. Once it reports ready,
Replay QA starts testing project ${args.projectId} automatically. Leave the command running for the
entire test; stopping it disconnects the test browsers from your app.

If the app's normal Next, Vite, Remix, or other development server must listen on a different port,
let the CLI wrap that server instead of adding a wrapper to the app:

\`\`\`sh
npx --yes replayqa run ${args.targetUrl} \\
  --upstream-url http://127.0.0.1:<dev-server-port> \\
  --no-app \\
  --project ${args.projectId} \\
  --qa-url ${args.qaUrl}
\`\`\`

The target URL remains Replay QA's stable browser-visible origin and \`--upstream-url\` is the
existing development server. Omit \`--no-app\` and add \`--app-command "<existing dev command>"\`
when the CLI should start it too. The upstream must serve the complete frontend and API needed by
the app. This command uses project ${args.projectId}; do not pass \`--create-project\`.

Emulation is target-specific. Preserve an app's existing emulator setup and endpoint overrides, and
configure extra browser-reachable hosts with \`--allow\`. Do not add emulation or create target
configuration merely to connect this existing project.

If Replay login is required, run this once and then retry the proxy command:

\`\`\`sh
npx --yes replayqa login
${command}
\`\`\`

If setup, DNS, or frpc fails, let the CLI repair managed dependencies and print a focused diagnosis,
then retry the proxy command:

\`\`\`sh
npx --yes replayqa doctor --fix --project ${args.projectId}
${command}
\`\`\`

The proxy allowlists ${allowHost} by default. If the app also calls an API, auth provider, or CDN
through this computer, include only those hosts:

\`\`\`sh
${command} --allow "${allowHost},api.example.com,auth.example.com"
\`\`\`
`
}
